import PoiStatusIcon from '@components/Shared/PoiStatusIcon';
import { poiNoticeType } from '@lib/spproject/inspectionStatus';
import type { Inspection } from '@lib/spproject/types';
import { Badge, Box, Stack, Typography } from '@mui/material';
import { FileText, ImageIcon } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';
import {
  InspectionRowBody,
  InspectionRowCard,
  MoreResourcesAvatar,
  PoiDescription,
  PoiInfo,
  PoiTitle,
  ResourceAvatar,
  StatusAvatar,
} from './Inspections.styled';
import useNoticeColors from '@store/hooks/useNoticeColors';

interface InspectionPointItemProps {
  poi: Inspection.PointOfInterest;
  inspectionId: string;
  onPoiClick: (inspectionId: string, poi: Inspection.PointOfInterest) => void;
}

/**
 * One point of interest, drawn as a row of the inspection list is: its own card, the same
 * border, hover and inner spacing. The two lists sit one click apart, so they are the same
 * components rather than two things that resemble each other.
 */
const InspectionPointItem = memo<InspectionPointItemProps>(({ poi, inspectionId, onPoiClick }) => {
  const noticeColors = useNoticeColors();

  const currentIndex = useMemo(() => {
    const idx = poi.stepIndex ?? 0;
    const max = Math.max(0, poi.steps.length - 1);
    return Math.min(Math.max(0, idx), max);
  }, [poi.stepIndex, poi.steps.length]);

  const currentStep = poi.steps[currentIndex];
  // The row stands for the whole point, so its status is the point's — the worst of its
  // steps — while the thumbnails beside it stay the step the point opens on.
  const noticeType = poiNoticeType(poi) ?? 'NotChecked';

  const resources = useMemo(() => currentStep?.resources ?? [], [currentStep?.resources]);
  const visibleResources = useMemo(() => {
    return resources.length > 3 ? resources.slice(0, 3) : resources;
  }, [resources]);

  const extraCount = resources.length > 3 ? (resources.length - 3) : 0;
  const handleClick = useCallback(() => {
    onPoiClick(inspectionId, poi);
  }, [inspectionId, poi, onPoiClick]);

  return (
    <InspectionRowCard onClick={handleClick}>
      <InspectionRowBody sx={{ py: 1.5 }}>
        <Badge
          sx={{ marginRight: '16px' }}
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <Box sx={{ backgroundColor: '#ccc', borderRadius: '100%', padding: '4px' }}>
              <PoiStatusIcon status={noticeType} />
            </Box>
          }>
          <StatusAvatar statusColor={noticeColors[noticeType]}>
            {poi.order}
          </StatusAvatar>
        </Badge>
        <PoiInfo sx={{ minWidth: 0 }}>
          <PoiTitle>
            <Typography variant="body2" fontWeight="medium" noWrap>
              {poi.name}
            </Typography>
          </PoiTitle>
          {poi.description && <PoiDescription variant="body2" noWrap>{poi.description}</PoiDescription>}
        </PoiInfo>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Box sx={{ display: 'flex', ml: 2 }}>
            {visibleResources.map((resource, i) => (
              <ResourceAvatar key={i} index={i}>
                {resource.type === 'Pdf'
                  ? <FileText size={20} color="#f44336" />
                  : <ImageIcon size={20} color="#2196f3" />}
              </ResourceAvatar>
            ))}
            {extraCount > 0 && (
              <MoreResourcesAvatar>+{extraCount}</MoreResourcesAvatar>
            )}
          </Box>
        </Stack>
      </InspectionRowBody>
    </InspectionRowCard>
  );
});

export default InspectionPointItem;
